/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useMemo, useRef, useState} from 'react'
import * as Yup from 'yup'
import {ErrorMessage, Field, FieldArray, Form, Formik} from 'formik'
import {ToastContainer} from 'react-toastify'

import LoadingComponent from '../../../components/LoadingComponent/LoadingComponent'
import QrCodeScanner from '../../../components/QrCodeScanner/QrCodeScanner'
import notifyToast from '../../../components/Toast/Toast'
import {fetchTable} from '../../../api/tableRS'
import {saveTablePromet} from '../../../api/tablePromet'
import LokacijeRsTraka from '../../../components/LokacijeRSTraka/LokacijeRsTraka'
import {KTIcon} from '../../../../_websoft/helpers'

type TrakaRow = {
    TrakaDisplay: string // full QR string (for display) OR id-only
    TrakaID: string // parsed ID (first segment)
    NaKolicinu: number | ''
    Lokacija: any

    Skenirano: number // 0/1 (final computed)

    // ✅ tracking like KrajRolne
    RawScan: string // raw payload that came from device OR full-QR in input
    ScannedByDevice: number // 0/1
}

type FormValues = {
    BrojListe: string
    trake: TrakaRow[]
}

/** Zebra + general cleanup */
const normalizeScan = (value: string) =>
    String(value ?? '')
        .replace(/[\r\n\t]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

/** robust company match: "Alternativa", "Alternativa doo", "Alternativa doo." etc */
const isCompanyAlternativa = (s: string) => {
    const cleaned = normalizeScan(s).toLowerCase().replace(/[^a-z]/g, '')
    return cleaned === 'alternativa' || cleaned === 'alternativadoo'
}

/**
 * ✅ Full traka QR payload (robust):
 * - split by ';'
 * - ignore empty parts (handles trailing ';')
 * - need at least 4 parts (>=3 semicolons)
 * - last meaningful part must be Alternativa or Alternativa doo
 */
const isFullTrakaQr = (raw: string) => {
    const s = normalizeScan(raw)
    if (!s) return false

    const parts = s
        .split(';')
        .map((p) => p.trim())
        .filter(Boolean)

    if (parts.length < 4) return false

    const last = parts[parts.length - 1] ?? ''
    return isCompanyAlternativa(last)
}

/** If scanner provides only ID -> treat as scanned (when it came from device) */
const isIdOnly = (raw: string) => /^\d+$/.test(normalizeScan(raw))

const extractTrakaIdFromQr = (raw: string): string => {
    const first = String(raw || '').split(';')[0] ?? ''
    return first.trim()
}

/**
 * Zebra keyboard-wedge scanning:
 * - chars arrive very quickly
 * - usually ends with Enter/Tab
 */
function useKeyboardScanner(onScan: (value: string) => void) {
    const bufferRef = useRef('')
    const lastTsRef = useRef<number>(0)

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const ts = Date.now()
            const delta = ts - lastTsRef.current
            lastTsRef.current = ts

            // slow typing => manual => reset buffer
            if (delta > 60) bufferRef.current = ''

            // finish scan on Enter/Tab
            if (e.key === 'Enter' || e.key === 'Tab') {
                const buf = bufferRef.current
                bufferRef.current = ''
                if (buf && buf.length >= 4) onScan(buf)
                return
            }

            // ignore control keys
            if (e.key.length !== 1) return

            bufferRef.current += e.key
        }

        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [onScan])
}

const emptyTrakaRow = (): TrakaRow => ({
    TrakaDisplay: '',
    TrakaID: '',
    NaKolicinu: '',
    Lokacija: '',
    Skenirano: 0,
    RawScan: '',
    ScannedByDevice: 0,
})

const PrometTrake: React.FC = () => {
    const inputFirstRef = useRef<HTMLInputElement>(null)

    const [loading, setLoading] = useState(false)

    // per-row fetched info (so we don’t mutate Formik values directly)
    const [trakaInfo, setTrakaInfo] = useState<any[]>([])

    // Need access to latest Formik refs for Zebra listener
    const formikRef = useRef<{
        setFieldValue?: (field: string, value: any) => void
        values?: FormValues
    }>({})

    const initialValues: FormValues = {
        BrojListe: '',
        trake: [emptyTrakaRow()],
    }

    // ✅ BrojListe format: 6 digits + "/" + 2 digits (25/26/etc)
    const brojListeRegex = /^\d{6}\/\d{2}$/

    const validationSchema = Yup.object({
        BrojListe: Yup.string()
            .matches(brojListeRegex, 'Format: 000005/26 (6 brojeva / 2 broja)')
            .required('Broj liste je obavezan'),
        trake: Yup.array().of(
            Yup.object().shape({
                TrakaID: Yup.string().required('Broj trake je obavezan'),
                NaKolicinu: Yup.number().typeError('Unesite broj').required('Krajnja težina je obavezna'),
                Lokacija: Yup.mixed().required('Lokacija je obavezna'),
                Skenirano: Yup.number(),
            })
        ),
    })

    useEffect(() => {
        if (inputFirstRef.current) inputFirstRef.current.focus()
    }, [])

    const computeSkenirano = (scannedByDevice: number, rawScan: string) => {
        if (Number(scannedByDevice) !== 1) return 0
        const cleaned = normalizeScan(rawScan)
        return isIdOnly(cleaned) || isFullTrakaQr(cleaned) ? 1 : 0
    }

    const fetchTraka = async (trakaId: string, index: number) => {
        const numericId = Number(trakaId)
        if (!numericId || Number.isNaN(numericId)) {
            setTrakaInfo((prev) => {
                const next = [...prev]
                next[index] = undefined
                return next
            })
            return
        }

        try {
            const response: any = await fetchTable(numericId)
            setTrakaInfo((prev) => {
                const next = [...prev]
                next[index] = response
                return next
            })
        } catch (error: any) {
            notifyToast(false, error?.message ?? 'Greška pri učitavanju trake')
            setTrakaInfo((prev) => {
                const next = [...prev]
                next[index] = {message: error?.message ?? 'Greška'}
                return next
            })
        }
    }

    const clearRowInfo = (index: number) => {
        setTrakaInfo((prev) => {
            const next = [...prev]
            next[index] = undefined
            return next
        })
    }

    const applyScanToRow = (
        index: number,
        raw: string,
        setFieldValue: (field: string, value: any) => void,
        scannedByDevice: number
    ) => {
        const cleaned = normalizeScan(raw)
        if (!cleaned) return

        const id = extractTrakaIdFromQr(cleaned)

        // always update tracking fields
        setFieldValue(`trake[${index}].RawScan`, cleaned)
        setFieldValue(`trake[${index}].ScannedByDevice`, scannedByDevice)

        // show raw in input + extract ID
        setFieldValue(`trake[${index}].TrakaDisplay`, cleaned)
        setFieldValue(`trake[${index}].TrakaID`, id)

        // compute scanned flag
        const sk = computeSkenirano(scannedByDevice, cleaned)
        setFieldValue(`trake[${index}].Skenirano`, sk)

        // refresh info (and avoid stale)
        clearRowInfo(index)
        if (id) fetchTraka(id, index)
    }

    // ✅ Zebra handler (keyboard wedge)
    const handleZebraScan = (raw: string) => {
        const setFieldValue = formikRef.current.setFieldValue
        const values = formikRef.current.values
        if (!setFieldValue || !values) return

        const active = document.activeElement as HTMLElement | null
        if (!active) return

        // Our input has data-index
        const idxAttr = (active as any)?.dataset?.index
        const index = idxAttr !== undefined ? Number(idxAttr) : NaN
        if (Number.isNaN(index)) return

        applyScanToRow(index, raw, setFieldValue, 1)
    }

    // Attach zebra listener once
    useKeyboardScanner(handleZebraScan)

    const onSubmit = async (values: FormValues) => {
        setLoading(true)

        const promises = (values.trake ?? []).map((t) => {
            const payload = {
                TrakaID: t.TrakaID,
                NaKolicinu: t.NaKolicinu,
                BrojListe: values.BrojListe,
                Lokacija: t.Lokacija,
                Skenirano: t.Skenirano, // ✅ 0/1
                // RawScan: t.RawScan, // optional
                // ScannedByDevice: t.ScannedByDevice, // optional
            }

            return saveTablePromet(payload)
                .then((res: any) => {
                    notifyToast(true, res.message)
                    return res
                })
                .catch((err: any) => {
                    notifyToast(false, err?.message ?? 'Greška')
                    return err
                })
        })

        await Promise.allSettled(promises)
        setLoading(false)
    }

    return (
        <div className='w-100 bg-white'>
            <ToastContainer />

            <div className='container-fluid px-3 px-md-4 py-3 py-md-4'>
                <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2'>
                    <div>
                        <h2 className='fw-bolder text-dark mb-1'>Promet trake</h2>
                        <div className='text-muted small'>
                            Kamera skener = skenirano. Zebra (keyboard wedge) = fokusiraj polje “Broj trake (QR)” i skeniraj. Ručni unos se ne smatra skeniranjem
                            (osim ako zalijepiš puni QR).
                        </div>
                    </div>
                </div>

                <div className='mt-3 mt-md-4'>
                    {loading ? (
                        <LoadingComponent />
                    ) : (
                        <Formik<FormValues>
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            enableReinitialize={false}
                            onSubmit={onSubmit}
                        >
                            {(props) => {
                                const {values, setFieldValue} = props

                                // keep latest refs for Zebra
                                formikRef.current.values = values
                                formikRef.current.setFieldValue = setFieldValue

                                return (
                                    <Form>
                                        {/* Broj liste */}
                                        <div className='card shadow-sm border-0 mb-4'>
                                            <div className='card-body p-3 p-md-4'>
                                                <div className='row g-3 align-items-end'>
                                                    <div className='col-12 col-md-6'>
                                                        <label className='form-label fw-semibold required'>Broj liste</label>
                                                        <Field name='BrojListe'>
                                                            {({field}: any) => (
                                                                <input
                                                                    {...field}
                                                                    className='form-control form-control-lg'
                                                                    placeholder='000005/26'
                                                                />
                                                            )}
                                                        </Field>
                                                        <ErrorMessage name='BrojListe'>
                                                            {(msg) => <div className='text-danger small mt-1'>{msg}</div>}
                                                        </ErrorMessage>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <FieldArray name='trake'>
                                            {(fa) => {
                                                const {remove, insert} = fa

                                                return (
                                                    <div className='d-flex flex-column gap-3'>
                                                        {values.trake.map((row, index) => {
                                                            const info: any = trakaInfo?.[index]
                                                            const isScanned = Number(row.Skenirano) === 1

                                                            return (
                                                                <div key={index} className='card shadow-sm border-0'>
                                                                    <div className='card-body p-3 p-md-4'>
                                                                        <div className='d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-2'>
                                                                            <div className='d-flex align-items-center gap-2 flex-wrap'>
                                                                                <span className='badge bg-secondary'>#{index + 1}</span>

                                                                                <span className={`badge ${isScanned ? 'bg-success' : 'bg-danger'} d-inline-flex align-items-center gap-2`}>
                                          {isScanned ? (
                                              <>
                                                  <i className='bi bi-check-circle-fill' />
                                                  Skenirano
                                              </>
                                          ) : (
                                              <>
                                                  <i className='bi bi-x-circle-fill' />
                                                  Nije skenirano
                                              </>
                                          )}
                                        </span>

                                                                                <span className={`badge ${Number(row.ScannedByDevice) === 1 ? 'bg-info' : 'bg-light text-muted'}`}>
                                          {Number(row.ScannedByDevice) === 1 ? 'Kamera/Zebra' : 'Ručni unos'}
                                        </span>
                                                                            </div>

                                                                            <div className='d-flex gap-2'>
                                                                                <button
                                                                                    type='button'
                                                                                    className='btn btn-outline-secondary btn-sm'
                                                                                    onClick={() => {
                                                                                        insert(index + 1, emptyTrakaRow())
                                                                                        setTrakaInfo((prev) => {
                                                                                            const next = [...prev]
                                                                                            next.splice(index + 1, 0, undefined)
                                                                                            return next
                                                                                        })
                                                                                    }}
                                                                                >
                                                                                    <i className='bi bi-plus-lg me-1' />
                                                                                    Dodaj
                                                                                </button>

                                                                                <button
                                                                                    type='button'
                                                                                    className='btn btn-outline-danger btn-sm'
                                                                                    disabled={values.trake.length === 1}
                                                                                    onClick={() => {
                                                                                        remove(index)
                                                                                        setTrakaInfo((prev) => {
                                                                                            const next = [...prev]
                                                                                            next.splice(index, 1)
                                                                                            return next
                                                                                        })
                                                                                    }}
                                                                                >
                                                                                    <i className='bi bi-trash me-1' />
                                                                                    Ukloni
                                                                                </button>
                                                                            </div>
                                                                        </div>

                                                                        <hr className='my-3' />

                                                                        <div className='row g-3 align-items-end'>
                                                                            {/* Scanner */}
                                                                            <div className='col-12 col-sm-6 col-lg-2'>
                                                                                <label className='form-label fw-semibold'>Skeniraj</label>
                                                                                <div className='d-flex align-items-center gap-2'>
                                                                                    <QrCodeScanner
                                                                                        onScan={(raw: any) => {
                                                                                            // ✅ device scan
                                                                                            applyScanToRow(index, String(raw || ''), setFieldValue, 1)
                                                                                        }}
                                                                                    />
                                                                                    <div className='text-muted small d-none d-lg-block'>QR / Barcode</div>
                                                                                </div>
                                                                            </div>

                                                                            {/* Input where Zebra writes */}
                                                                            <div className='col-12 col-sm-6 col-lg-4'>
                                                                                <label className='form-label fw-semibold required'>Broj trake (QR)</label>

                                                                                <Field name={`trake[${index}].TrakaDisplay`}>
                                                                                    {({field}: any) => (
                                                                                        <input
                                                                                            {...field}
                                                                                            ref={index === 0 ? inputFirstRef : undefined}
                                                                                            data-index={index} // ✅ critical for Zebra mapping
                                                                                            className='form-control form-control-lg'
                                                                                            placeholder='Skeniraj ili upisi broj trake'
                                                                                            onChange={(e) => {
                                                                                                const rawVal = e.target.value
                                                                                                const cleaned = normalizeScan(rawVal)

                                                                                                // always show what user typed
                                                                                                setFieldValue(`trake[${index}].TrakaDisplay`, rawVal)

                                                                                                // clear old info immediately (fix stale)
                                                                                                clearRowInfo(index)

                                                                                                // extract id
                                                                                                const id = extractTrakaIdFromQr(cleaned)
                                                                                                setFieldValue(`trake[${index}].TrakaID`, id)

                                                                                                // ✅ If full QR is pasted/injected in input => treat as device scan
                                                                                                if (isFullTrakaQr(cleaned)) {
                                                                                                    setFieldValue(`trake[${index}].RawScan`, cleaned)
                                                                                                    setFieldValue(`trake[${index}].ScannedByDevice`, 1)
                                                                                                    setFieldValue(`trake[${index}].Skenirano`, 1)
                                                                                                    return
                                                                                                }

                                                                                                // manual typing => NOT scanned
                                                                                                setFieldValue(`trake[${index}].RawScan`, '')
                                                                                                setFieldValue(`trake[${index}].ScannedByDevice`, 0)
                                                                                                setFieldValue(`trake[${index}].Skenirano`, 0)
                                                                                            }}
                                                                                            onBlur={() => {
                                                                                                const rawStr = normalizeScan(String(field.value || ''))
                                                                                                const id = extractTrakaIdFromQr(rawStr)
                                                                                                setFieldValue(`trake[${index}].TrakaID`, id)

                                                                                                if (id) fetchTraka(id, index)
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                </Field>

                                                                                <ErrorMessage name={`trake[${index}].TrakaID`}>
                                                                                    {(msg) => <div className='text-danger small mt-1'>{msg}</div>}
                                                                                </ErrorMessage>

                                                                                <Field name={`trake[${index}].TrakaID`} type='hidden' />
                                                                            </div>

                                                                            {/* Krajnja težina */}
                                                                            <div className='col-12 col-sm-6 col-lg-3'>
                                                                                <label className='form-label fw-semibold required'>Krajnja težina</label>
                                                                                <Field name={`trake[${index}].NaKolicinu`}>
                                                                                    {({field}: any) => (
                                                                                        <input
                                                                                            {...field}
                                                                                            type='number'
                                                                                            className='form-control form-control-lg'
                                                                                            placeholder='0'
                                                                                            onChange={(e) => {
                                                                                                const raw = e.target.value
                                                                                                const newValue = raw === '' ? '' : Number(raw)

                                                                                                const maxRaw = info?.Tezina
                                                                                                const maxValue = maxRaw ? Number(maxRaw) : Number.MAX_SAFE_INTEGER

                                                                                                if (newValue !== '' && newValue > maxValue) {
                                                                                                    setFieldValue(field.name, maxValue)
                                                                                                } else {
                                                                                                    setFieldValue(field.name, newValue)
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                </Field>
                                                                                <ErrorMessage name={`trake[${index}].NaKolicinu`}>
                                                                                    {(msg) => <div className='text-danger small mt-1'>{msg}</div>}
                                                                                </ErrorMessage>
                                                                            </div>

                                                                            {/* Lokacija */}
                                                                            <div className='col-12 col-sm-6 col-lg-3'>
                                                                                <label className='form-label fw-semibold required'>Lokacija</label>
                                                                                <Field name={`trake[${index}].Lokacija`}>
                                                                                    {({field}: any) => (
                                                                                        <LokacijeRsTraka
                                                                                            value={field.value}
                                                                                            onChange={(name: any, value: any) => setFieldValue(`trake[${index}].Lokacija`, value)}
                                                                                            errors={(props.errors as any)?.trake?.[index]?.Lokacija}
                                                                                            touched={(props.touched as any)?.trake?.[index]?.Lokacija}
                                                                                        />
                                                                                    )}
                                                                                </Field>
                                                                                <ErrorMessage name={`trake[${index}].Lokacija`}>
                                                                                    {(msg) => <div className='text-danger small mt-1'>{msg}</div>}
                                                                                </ErrorMessage>
                                                                            </div>

                                                                            {/* hidden tracking */}
                                                                            <Field name={`trake[${index}].Skenirano`} type='hidden' />
                                                                            <Field name={`trake[${index}].RawScan`} type='hidden' />
                                                                            <Field name={`trake[${index}].ScannedByDevice`} type='hidden' />
                                                                        </div>

                                                                        {/* Info panel */}
                                                                        <div className='mt-3'>
                                                                            {!info ? (
                                                                                <div className='alert alert-light border mb-0'>
                                                                                    <span className='text-muted'>Upišite ili skenirajte broj trake da učitate detalje.</span>
                                                                                </div>
                                                                            ) : info?.message ? (
                                                                                <div className='alert alert-warning mb-0'>
                                                                                    <b>Poruka:</b> {info.message}
                                                                                </div>
                                                                            ) : (
                                                                                <div className='alert alert-primary mb-0'>
                                                                                    <div className='d-flex flex-column flex-md-row flex-wrap gap-2'>
                                                                                        <div>
                                                                                            Dužina: <b>{info.TrenutnaDuzina ?? 'N/A'}</b>
                                                                                        </div>
                                                                                        <div className='d-none d-md-block'>|</div>
                                                                                        <div>
                                                                                            Težina: <b>{info.Tezina ?? 'N/A'}</b>
                                                                                        </div>
                                                                                        <div className='d-none d-md-block'>|</div>
                                                                                        <div>
                                                                                            Širina: <b>{info.Sirina ?? 'N/A'}</b>
                                                                                        </div>
                                                                                        <div className='d-none d-md-block'>|</div>
                                                                                        <div>
                                                                                            Broj rolne: <b>{info.BrojRolne ?? 'N/A'}</b>
                                                                                        </div>
                                                                                        <div className='d-none d-md-block'>|</div>
                                                                                        <div>
                                                                                            Proizvođač:{' '}
                                                                                            <b>{info?.repromaterijal?.proizvodjac?.PartnerNaziv ?? 'N/A'}</b>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}

                                                        {/* Add row button */}
                                                        <div className='d-flex justify-content-start'>
                                                            <button type='button' className='btn btn-light-primary' onClick={() => insert(values.trake.length, emptyTrakaRow())}>
                                                                <KTIcon iconName='plus' className='fs-2 me-2' />
                                                                Dodaj još jednu traku
                                                            </button>
                                                        </div>
                                                    </div>
                                                )
                                            }}
                                        </FieldArray>

                                        {/* Footer */}
                                        <div className='card shadow-sm border-0 mt-4'>
                                            <div className='card-body d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3'>
                                                <div className='ms-md-auto ms-auto'>
                                                    <button type='submit' className='btn btn-success btn-lg px-4' disabled={loading}>
                                                        {!loading ? (
                                                            <>
                                                                <i className='bi bi-check2-circle me-2' />
                                                                Potvrdi
                                                            </>
                                                        ) : (
                                                            <span className='d-inline-flex align-items-center gap-2'>
                                Please wait...
                                <span className='spinner-border spinner-border-sm align-middle' />
                              </span>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </Form>
                                )
                            }}
                        </Formik>
                    )}
                </div>
            </div>
        </div>
    )
}

export default PrometTrake
